import React from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import {
  FaBell,
  FaCalendarAlt,
  FaCog,
  FaHome,
  FaLayerGroup,
  FaUsers,
  FaUserShield,
  FaCreditCard,
} from "react-icons/fa";

import Dashboard from "../../pages/Admin/Dashboard/Dashboard";
import Users from "../../pages/Admin/Users/Users";
import AdherentsAdmin from "../../pages/Admin/AdherentsAdmin/AdherentsAdmin";
import EventList from "../../pages/Admin/EventList/EventList";
import GroupsAdmin from "../../pages/Admin/GroupsAdmin/GroupsAdmin";
import NotificationList from "../../pages/Admin/NotificationList/NotificationList";
import Settings from "../../pages/Admin/Settings/Settings";
import ErrorPage from "../../pages/ErrorPage/ErrorPage";
import SubscriptionsAdmin from "../../pages/Admin/Subscriptions/SubscriptionsAdmin";

import { useSidebar } from "../../contexts/SidebarContext";

const PAGE_META = {
  "/admin/dashboard": {
    label: "Tableau de bord",
    description: "Vue globale de l’activité et des données principales.",
    icon: FaHome,
  },
  "/admin/utilisateurs": {
    label: "Utilisateurs",
    description: "Gestion des comptes, rôles et accès à l’application.",
    icon: FaUserShield,
  },
  "/admin/adherents": {
    label: "Adhérents",
    description: "Gestion des judokas, profils sportifs et rattachements.",
    icon: FaUsers,
  },
  "/admin/evenements": {
    label: "Événements",
    description: "Gestion des compétitions, stages et inscriptions.",
    icon: FaCalendarAlt,
  },
  "/admin/groupes": {
    label: "Groupes",
    description: "Gestion des groupes, coachs et adhérents associés.",
    icon: FaLayerGroup,
  },
  "/admin/notifications": {
    label: "Notifications",
    description: "Suivi des alertes et messages système.",
    icon: FaBell,
  },
  "/admin/abonnements": {
    label: "Abonnements",
    description: "Gestion des achats et renouvellements d'abonnements.",
    icon: FaCreditCard,
  },
  "/admin/parametres": {
    label: "Paramètres",
    description: "Configuration générale du site et de l’application.",
    icon: FaCog,
  },
};

function AdminPageHeader() {
  const location = useLocation();

  const meta = PAGE_META[location.pathname] || {
    label: "Administration",
    description: "Espace d’administration Judo Coach Pro.",
    icon: FaHome,
  };

  const Icon = meta.icon;

  return (
    <header className="mb-6 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/20">
      <div className="relative bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 p-5 text-white sm:p-6">
        <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-sky-400/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-24 w-24 rounded-full bg-indigo-400/20 blur-3xl" />

        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[1.25rem] bg-white/15 text-2xl backdrop-blur-xl">
              <Icon />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-200/70">
                Admin Judo Coach Pro
              </p>

              <h1 className="mt-2 font-titre text-3xl font-bold tracking-tight sm:text-4xl">
                {meta.label}
              </h1>

              <p className="mt-2 max-w-4xl text-sm font-medium leading-6 text-white/65">
                {meta.description}
              </p>
            </div>
          </div>

          <div className="rounded-[1.5rem] border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-xl">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-white/45">
              Mode
            </p>
            <p className="mt-1 text-sm font-bold text-white">
              Administration complète
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}

function MainContent() {
  const { isCollapsed } = useSidebar();

  return (
    <main
      className={`min-h-screen bg-slate-50 pt-[104px] text-slate-950 transition-all duration-300 dark:bg-slate-950 dark:text-white ${
        isCollapsed ? "lg:ml-[112px]" : "lg:ml-[320px]"
      }`}
    >
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.12),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(79,70,229,0.12),transparent_28%)]" />

      <div className="w-full overflow-x-auto pb-24 pr-4 sm:pr-6 lg:pr-8">
        <div className="mx-auto w-[1600px] max-w-none">
          <AdminPageHeader />

          <section className="min-h-[calc(100vh-240px)] rounded-[2rem] border border-slate-200 bg-white/80 p-4 shadow-2xl shadow-slate-200/60 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80 dark:shadow-black/20 sm:p-6">
            <Routes>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="utilisateurs" element={<Users />} />
              <Route path="adherents" element={<AdherentsAdmin />} />
              <Route path="evenements" element={<EventList />} />
              <Route path="groupes" element={<GroupsAdmin />} />
              <Route path="notifications" element={<NotificationList />} />
              <Route path="abonnements" element={<SubscriptionsAdmin />} />
              <Route path="parametres" element={<Settings />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </section>
        </div>
      </div>
    </main>
  );
}

export default MainContent;